// Interface para Estado (UF) do IBGE
export interface EstadoIBGE {
  id: number;
  sigla: string;
  nome: string;
}

// Interface para Município do IBGE
export interface MunicipioIBGE {
  id: number;
  nome: string;
  microrregiao: {
    id: number;
    nome: string;
    mesorregiao: {
      id: number;
      nome: string;
      UF: {
        id: number;
        sigla: string;
        nome: string;
      };
    };
  };
}

// Interface simplificada para uso no formulário
export interface Estado {
  sigla: string;
  nome: string;
}

export interface Municipio {
  id: number;
  nome: string;
}

/**
 * Busca todos os estados (UFs) do Brasil
 * @returns Promise com array de estados
 */
export const buscarEstados = async (): Promise<Estado[]> => {
  try {
    const response = await fetch('https://servicodados.ibge.gov.br/api/v1/localidades/estados');
    
    if (!response.ok) {
      throw new Error('Erro na requisição para API do IBGE - Estados');
    }

    const estados: EstadoIBGE[] = await response.json();
    
    // Ordena os estados por nome e retorna formato simplificado
    return estados
      .sort((a, b) => a.nome.localeCompare(b.nome))
      .map(estado => ({
        sigla: estado.sigla,
        nome: estado.nome
      }));
  } catch (error) {
    console.error('Erro ao buscar estados:', error);
    return [];
  }
};

/**
 * Busca municípios por estado (UF)
 * @param siglaUF - Sigla do estado (ex: 'SP', 'RJ')
 * @returns Promise com array de municípios
 */
export const buscarMunicipiosPorEstado = async (siglaUF: string): Promise<Municipio[]> => {
  try {
    if (!siglaUF || siglaUF.length !== 2) {
      return [];
    }

    const response = await fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${siglaUF}/municipios`);
    
    if (!response.ok) {
      throw new Error('Erro na requisição para API do IBGE - Municípios');
    }

    const municipios: MunicipioIBGE[] = await response.json();
    
    // Ordena os municípios por nome e retorna formato simplificado
    return municipios
      .sort((a, b) => a.nome.localeCompare(b.nome))
      .map(municipio => ({
        id: municipio.id,
        nome: municipio.nome
      }));
  } catch (error) {
    console.error('Erro ao buscar municípios:', error);
    return [];
  }
};

/**
 * Busca informações de um município específico
 * @param idMunicipio - ID do município no IBGE
 * @returns Promise com dados do município ou null
 */
export const buscarMunicipioPorId = async (idMunicipio: number): Promise<{ municipio: string; estado: string; uf: string } | null> => {
  try {
    const response = await fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/municipios/${idMunicipio}`);
    
    if (!response.ok) {
      throw new Error('Erro na requisição para API do IBGE - Município');
    }

    

    const municipio: MunicipioIBGE = await response.json();
    return {
      municipio: municipio.nome,
      estado: municipio.microrregiao.mesorregiao.UF.nome,
      uf: municipio.microrregiao.mesorregiao.UF.sigla
    };
  } catch (error) {
    console.error('Erro ao buscar município:', error);
    return null;
  }
};
